import { useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import useAuthStore from '@/stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresSubscription?: boolean;
  requiresEmailVerification?: boolean;
}

export default function ProtectedRoute({
  children,
  requiresSubscription = false,
  requiresEmailVerification = false
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, organization, user, loadUser } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Detect successful return from Stripe Checkout or presence of a session id
  const hasCheckoutSuccess = searchParams.get('checkout') === 'success' || searchParams.has('session_id');

  // Allowlist of admin emails that bypass subscription requirement
  const BYPASS_EMAILS = ['sophie@quarryside.com', 'sophie@quarrygrove.com'];
  const isSubscriptionBypassUser = () => {
    const email = (user as any)?.email || '';
    return !!email && BYPASS_EMAILS.includes(String(email).toLowerCase());
  };

  // On checkout success, proactively refresh the user/org to pick up updated subscription status
  useEffect(() => {
    if (requiresSubscription && hasCheckoutSuccess && typeof loadUser === 'function') {
      // Fire and forget – avoid blocking navigation
      loadUser().catch(() => {
        // Ignore refresh errors; guard below will still allow once on success return
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresSubscription, hasCheckoutSuccess]);

  // Record a short-lived success marker to bridge webhook delays and clean query params
  useEffect(() => {
    if (hasCheckoutSuccess) {
      try {
        localStorage.setItem('rr_last_checkout_success_at', String(Date.now()));
      } catch {}
      // Clean up URL to avoid confusing users/bookmarks
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('checkout');
        url.searchParams.delete('session_id');
        window.history.replaceState({}, '', url.toString());
      } catch {}
    }
  }, [hasCheckoutSuccess]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check email verification requirement
  const emailVerified = Boolean((user as any)?.emailVerified);
  if (requiresEmailVerification && !emailVerified) {
    // Allow access to email verification page itself
    if (location.pathname !== '/email-verification') {
      return <Navigate to="/email-verification" state={{ from: location }} replace />;
    }
  }

  // Check if user should see welcome page
  const isWelcome = searchParams.get('welcome') === 'true';
  const isPaymentSuccess = searchParams.get('payment') === 'success';
  
  // If coming from signup or payment success, show welcome page
  if ((isWelcome || isPaymentSuccess) && location.pathname !== '/welcome') {
    const welcomeUrl = `/welcome?${searchParams.toString()}`;
    return <Navigate to={welcomeUrl} replace />;
  }

  // For subscription-required routes, enforce active or trialing subscription
  if (requiresSubscription) {
    // Allow bypass accounts to proceed without subscription
    if (isSubscriptionBypassUser()) {
      return <>{children}</>;
    }
    const subscription = (organization as any)?.billing?.subscriptions?.riderelease || (organization as any)?.subscription;
    const status = String(subscription?.status || '').toLowerCase();
    const hasActiveSubscription = status === 'active';
    const isInTrial = status === 'trial' || status === 'trialing';

    // Consider checkout success (or recent success) as a temporary allow to avoid bouncing back to billing
    let recentCheckoutSuccess = false;
    try {
      const ts = Number(localStorage.getItem('rr_last_checkout_success_at') || '0');
      // Allow for up to 60 minutes after success to cover webhook propagation
      recentCheckoutSuccess = ts > 0 && (Date.now() - ts) < 60 * 60 * 1000;
    } catch {}

    const allowAccess = hasActiveSubscription || isInTrial || hasCheckoutSuccess || recentCheckoutSuccess;
    if (!allowAccess) {
      return <Navigate to="/signup?step=3&onboarding=true" replace />;
    }
  }

  return <>{children}</>;
}